"use client";
import React from "react";
import { Box, Typography, Button, Container } from "@mui/material";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";
import NextLink from "next/link";

// role text color per agent (matches reference design)
const AGENT_COLORS = {
  ZAYN: "#7A2BF5",
  ATLAS: "#1D8FDC",
  SAGE: "#E2622F",
  SCOUT: "#A855F7",
  COMPASS: "#D4790B",
  GOKU: "#C07C0A", // OMNI
};

// route each agent card links to
const AGENT_ROUTES = {
  ZAYN: "/ai-agents/zayn",
  SCOUT: "/ai-agents/scout",
  SAGE: "/ai-agents/sage",
  ATLAS: "/ai-agents/atlas",
  COMPASS: "/ai-agents/compass",
  GOKU: "/ai-agents/omni", // GOKU = OMNI
};

const AgentCard = ({ agent, colorKey, href }) => (
  <Box
    component={NextLink}
    href={href}
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 0.5,
      px: { xs: 2, sm: 2.75 },
      py: { xs: 1.25, sm: 1.5 },
      borderRadius: "16px",
      backgroundColor: "#fff",
      border: "1px solid rgba(0,0,0,0.06)",
      boxShadow: "0 4px 16px rgba(120,80,200,0.06)",
      transition: "all 0.3s ease",
      cursor: "pointer",
      width: "100%",
      textDecoration: "none",
      color: "inherit",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 10px 30px rgba(120,80,200,0.15)",
      },
    }}
  >
    <Box
      component="img"
      src={agent.image}
      alt={agent.name}
      sx={{
        width: { xs: 32, sm: 38, md: 40 },
        height: { xs: 32, sm: 38, md: 40 },
        borderRadius: "50%",
        objectFit: "cover",
        flexShrink: 0,
        mb: 0.25,
      }}
    />
    <Typography
      sx={{
        fontFamily: "var(--font-outfit)",
        fontWeight: 700,
        fontSize: { xs: "0.78rem", sm: "0.85rem", md: "0.9rem" },
        letterSpacing: "0.08em",
        color: "#1a1a2e",
        textAlign: "left",
      }}
    >
      {agent.name}
    </Typography>
    <Typography
      sx={{
        fontFamily: "var(--font-outfit)",
        fontWeight: 500,
        fontSize: { xs: "0.68rem", sm: "0.75rem", md: "0.8rem" },
        color: AGENT_COLORS[colorKey] || "#6b7280",
        textAlign: "left",
        lineHeight: 1.3,
      }}
    >
      {agent.role}
    </Typography>
  </Box>
);

const AIAgentMainSection = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  // order: ZAYN, SCOUT, SAGE, ATLAS, COMPASS, GOKU
  const agentKeys = ["ZAYN", "SCOUT", "SAGE", "ATLAS", "COMPASS", "GOKU"];
  const keyToImage = {
    ZAYN: IMAGES.ZAYN_CIRCLE,
    ATLAS: IMAGES.ATLAS_CIRCLE,
    SAGE: IMAGES.SAGE_CIRCLE,
    SCOUT: IMAGES.SCOUT_CIRCLE,
    COMPASS: IMAGES.COMPASS_CIRCLE,
    GOKU: IMAGES.GOKU_CIRCLE,
  };

  const AGENTS = agentKeys.map((key) => ({
    key,
    name: t(`aiAgentsSection.agents.${key}.name`),
    role: t(`aiAgentsSection.agents.${key}.role`),
    image: keyToImage[key],
  }));

  return (
    <Box
      sx={{
        backgroundImage: `url("${IMAGES.AI_AGENT_BG}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        // minHeight: "100vh",
        width: "100%",
        pt: { xs: "80px", sm: "90px", md: "150px" },
        pb: { xs: 6, sm: 8, md: 8 },
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "flex-start" },
            justifyContent: "space-between",
            gap: { xs: 5, md: 4 },
          }}
        >
          {/* LEFT: text content */}
          <Box
            sx={{
              flex: { md: "0 0 48%" },
              maxWidth: { xs: "100%", md: "44%" },
              textAlign: isArabic ? "right" : "left",
            }}
          >
            {/* Badge */}
            <Box
              display="flex"
              justifyContent="flex-start"
              mb={{ xs: 2, sm: 2.5, md: 3 }}
            >
              <Box
                sx={{
                  border: "1.5px solid #FFB200",
                  borderRadius: "50px",
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.7, sm: 1 },
                  backgroundColor: "#fff",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: "#FFB200",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "var(--font-outfit)",
                    fontSize: { xs: "0.78rem", sm: "0.9rem", md: "0.95rem" },
                    color: "#738494",
                    fontWeight: 500,
                  }}
                >
                  {t("aiAgentsSection.badge")}
                </Typography>
              </Box>
            </Box>

            {/* Heading */}
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontFamily: "var(--font-plusjakarta)",
                fontWeight: 700,
                fontSize: { xs: "1.9rem", sm: "2.6rem", md: "3.1rem" },
                lineHeight: { xs: 1.3, sm: 1.25, md: 1.25 },
                color: "#1a1a2e",
                mb: { xs: 2, sm: 2.5 },
              }}
            >
              {t("aiAgentsSection.heading1")}{" "}
              <Box component="span" sx={{ color: "#a93afe" }}>
                {t("aiAgentsSection.heading2")}
              </Box>
              {t("aiAgentsSection.heading3")}
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                fontFamily: "var(--font-outfit)",
                fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                color: "#5D697D",
                lineHeight: 1.7,
                mb: 3,
              }}
            >
              {t("aiAgentsSection.desc1")}
            </Typography>

            {/* CTA row: button + trusted line */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "flex-start",
                gap: { xs: 1.5, sm: 2.5 },
              }}
            >
              <NextLink
                href="/free-trial-of-ai-recruiting-tool"
                passHref
                legacyBehavior
              >
                <Button
                  component="a"
                  rel="noopener noreferrer"
                  sx={{
                    background:
                      "linear-gradient(135deg, #a855f7 0%, #8b5cf6 50%, #7c3aed 100%)",
                    color: "#fff",
                    fontFamily: "var(--font-outfit)",
                    fontWeight: 600,
                    fontSize: { xs: "0.85rem", sm: "1rem", md: "1.05rem" },
                    px: { xs: 3, sm: 3.5, md: 4 },
                    py: { xs: 1.3, sm: 1.5, md: 1.5 },
                    borderRadius: "50px",
                    textTransform: "none",
                    boxShadow: "none",
                    whiteSpace: "nowrap",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #9333ea 0%, #7c3aed 50%, #6d28d9 100%)",
                      boxShadow: "none",
                      transform: "scale(1.03)",
                    },
                    "&:active": {
                      transform: "scale(0.98)",
                    },
                  }}
                >
                  {t("aiAgentsSection.tryButton")}
                </Button>
              </NextLink>

              <Typography
                sx={{
                  fontFamily: "var(--font-outfit)",
                  fontSize: { xs: "0.78rem", sm: "0.85rem" },
                  color: "#5D697D",
                }}
              >
                {t("aiAgentsSection.desc2")}
              </Typography>
            </Box>
          </Box>

          {/* RIGHT: agent cards grid */}
          <Box
            sx={{
              flex: { md: "0 0 48%" },
              width: { xs: "100%", md: "48%" },
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: { xs: 1.5, sm: 1.75 },
              mt: { xs: 2, sm: "5%", md: "12%", xl: "9%" },
            }}
          >
            {AGENTS.map((agent) => (
              <AgentCard
                key={agent.key}
                agent={agent}
                colorKey={agent.key}
                href={AGENT_ROUTES[agent.key]}
              />
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default AIAgentMainSection;